import { useState, useMemo, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Calculator, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { getVisitorId, trackEvent } from "../../lib/track";
import { getCollateral } from "../../lib/collateral";
import { IMAGES, COMPANY } from "../../lib/data";
import { ModelingDisclaimer } from "../../components/site/ModelingDisclaimer";

const usd = (n) => "$" + Math.round(n).toLocaleString();

export default function ProductROICalculator() {
  const { product } = useParams();
  const c = getCollateral(product);
  const cfg = c?.calculator;
  const initial = useMemo(() => (cfg ? Object.fromEntries(cfg.fields.map((f) => [f.key, f.def])) : {}), [cfg]);
  const [v, setV] = useState(initial);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const r = useMemo(() => (cfg ? cfg.compute(v) : null), [cfg, v]);

  useEffect(() => {
    if (c && cfg) trackEvent("calculator_open", { label: `${c.name} — ROI Calculator`, product: c.name });
  }, [c, cfg]);

  if (!c || !cfg) return <Navigate to="/solutions" replace />;

  const set = (k, val) => setV((p) => ({ ...p, [k]: val === "" ? "" : Number(val) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setSending(true);
    try {
      await api.post("/quote", { name: form.name, email: form.email, company: form.company || "—", industry: `${c.name} ROI Calculator`, message: r.summary, visitor_id: getVisitorId() });
      await trackEvent("calculator_complete", { email: form.email, label: `${c.name} — ROI Calculator completed`, product: c.name });
      setSent(true);
      toast.success("Analysis sent. Our team will follow up.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-charcoal text-white">
      <div className="sticky top-0 z-40 bg-ink border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to={`/products/${c.slug}`} className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm"><ArrowLeft className="h-4 w-4" /> Back to product</Link>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/50"><Calculator className="h-3.5 w-3.5 text-flame" /> {c.name} ROI Calculator</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-2"><img src={IMAGES.markWhite} alt="OneHarvest" className="h-7 w-7 object-contain" /><span className="font-display font-extrabold tracking-tight text-sm">ONEHARVEST</span></div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">{cfg.headline}</h1>
        <p className="mt-3 text-white/60 max-w-2xl">{cfg.sub} Adjust the inputs — the numbers update live. Email yourself the full analysis.</p>

        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 bg-ink border border-white/10 p-6" data-testid="calc-inputs">
            <h2 className="font-display font-bold text-lg mb-4">Your Assumptions</h2>
            <div className="space-y-4">
              {cfg.fields.map((f) => (
                <div key={f.key}>
                  <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-1">{f.label}</label>
                  <input type="number" min="0" step={f.step || 1} value={v[f.key]} data-testid={`input-${f.key}`} onChange={(e) => set(f.key, e.target.value)} className="w-full bg-charcoal border border-white/15 px-3 py-2.5 font-mono text-white focus:border-flame focus:outline-none" />
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="calc-results">
              {r.cards.map((cd) => (
                <div key={cd.l} className="bg-ink border border-white/10 p-4"><div className={`font-display font-extrabold text-2xl ${cd.flame ? "text-flame" : "text-white"}`}>{cd.v}</div><div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">{cd.l}</div></div>
              ))}
            </div>
            <div className="bg-ink border border-white/10 p-6">
              <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                <div className="bg-ink p-4"><div className="text-[10px] uppercase tracking-wider text-white/50">{r.box.aLabel}</div><div className="mt-2 font-display font-extrabold text-2xl text-white/85">{usd(r.box.aVal)}</div></div>
                <div className="bg-ink p-4"><div className="text-[10px] uppercase tracking-wider text-white/50">{r.box.bLabel}</div><div className="mt-2 font-display font-extrabold text-2xl text-white">{usd(r.box.bVal)}</div></div>
                <div className="bg-flame p-4"><div className="text-[10px] uppercase tracking-wider text-white/80">{r.box.cLabel}</div><div className="mt-2 font-display font-extrabold text-2xl text-white" data-testid="calc-savings">{usd(r.box.cVal)}</div></div>
              </div>
            </div>
            <div className="bg-ink border border-flame/40 p-6">
              {sent ? (
                <div className="flex items-center gap-3 text-flame" data-testid="calc-sent"><CheckCircle2 className="h-6 w-6" /><div><div className="font-display font-bold text-white">Analysis sent to your team.</div><div className="text-sm text-white/60">We'll follow up within one business day.</div></div></div>
              ) : (
                <form onSubmit={submit} data-testid="calc-lead-form">
                  <h3 className="font-display font-bold text-lg">Email me this analysis</h3>
                  <div className="mt-4 grid sm:grid-cols-3 gap-3">
                    <input placeholder="Full name" value={form.name} data-testid="lead-name" onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                    <input type="email" placeholder="Work email" value={form.email} data-testid="lead-email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                    <input placeholder="Company" value={form.company} data-testid="lead-company" onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                  </div>
                  <button type="submit" disabled={sending} data-testid="calc-submit" className="mt-4 inline-flex items-center gap-2 bg-flame text-white px-6 py-3 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-60">{sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send my analysis"}</button>
                </form>
              )}
            </div>
            <ModelingDisclaimer dark testid="calc-disclaimer" />
          </div>
        </div>
      </div>
    </div>
  );
}
